<script>
import Layout from "@/router/layouts/main";
import Breadcrumb from "@/components/Breadcrumb";
import { api } from "@/state/services";
import { formatPoints, errorMessage, uuid } from "@/state/helpers/experiences";

// Confirmação do resgate: pagamento único com o saldo de pontos
export default {
  components: { Layout, Breadcrumb },
  data() {
    return {
      loading: true,
      errored: false,
      empty: false,
      experience: null,
      balance: 0,
      accepted: false,
      submitting: false,
      // Chave de idempotência gerada ao montar a tela
      idempotency: uuid(),
    };
  },
  computed: {
    balanceAfter() {
      return this.experience ? this.balance - this.experience.points : 0;
    },
  },
  methods: {
    formatPoints,
    load() {
      this.loading = true;
      this.errored = false;
      this.empty = false;

      return api
        .get("experiences/" + this.$route.params.id)
        .then((response) => {
          if (response.data.status == "success") {
            this.experience = response.data.experience;
            this.balance = Number(response.data.balance) || 0;
          } else {
            this.empty = true;
          }
        })
        .catch(() => {
          this.errored = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    redeem() {
      if (this.submitting || !this.accepted) {
        return;
      }
      this.submitting = true;
      this.$bvModal.show("ex-processing");

      const formData = new FormData();
      formData.append("experience", this.experience.id);
      formData.append("idempotency", this.idempotency);

      api
        .post("experiences/redeem", formData)
        .then((response) => {
          if (response.data.status == "success") {
            this.$toast.success(response.data.message);
            this.$router.replace("/experiences/vouchers/" + response.data.voucher.protocol);
          } else {
            this.$toast.error(response.data.message);
            this.load();
          }
        })
        .catch((error) => {
          this.$toast.error(errorMessage(error, "Não foi possível registrar o resgate, tente novamente."));
          this.load();
        })
        .finally(() => {
          this.submitting = false;
          this.$bvModal.hide("ex-processing");
        });
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<template>
  <Layout>
    <Breadcrumb :items="[{ text: 'Experiências', to: '/experiences' }, { text: 'Confirmar resgate' }]" />

    <div v-if="loading && !experience" class="text-center py-5">
      <b-spinner variant="default" role="status"></b-spinner>
    </div>
    <div v-else-if="errored" class="text-center py-5">
      <p>Pedimos desculpas, não podemos recuperar as informações neste momento.</p>
      <button class="btn btn-default" v-on:click="load()">Tentar novamente</button>
    </div>
    <div v-else-if="empty" class="ds-page">
      <div class="ds-box ds-result text-center">
        <i class="bx bx-error-circle ds-result-icon" aria-hidden="true"></i>
        <h4>Experiência não encontrada</h4>
        <router-link tag="a" to="/experiences" class="btn btn-default btn-lg btn-block text-uppercase mt-4">Ver experiências</router-link>
      </div>
    </div>
    <div v-else-if="experience" class="ds-page">
      <div class="ds-intro">
        <h1 class="ds-title">Confirmar resgate</h1>
      </div>

      <div class="ds-grid">
        <div class="ds-box ds-summary">
          <h5 class="mb-3">Resumo</h5>
          <div class="ex-product mb-3">
            <div class="ex-cover-mini ex-cover-sm">
              <div class="ex-cover">
                <img v-if="experience.image" :src="experience.image" :alt="experience.title">
                <span v-else class="ex-cover-empty"><i class="bx bx-gift font-size-20" aria-hidden="true"></i></span>
              </div>
            </div>
            <strong>{{ experience.title }}</strong>
          </div>
          <dl>
            <div>
              <dt>Parceiro</dt>
              <dd>{{ experience.partner.name }}</dd>
            </div>
            <div class="is-total">
              <dt>Total</dt>
              <dd>{{ formatPoints(experience.points) }}</dd>
            </div>
          </dl>
        </div>

        <div class="ds-box">
          <h5 class="mb-3">Resgatar com pontos</h5>
          <template v-if="experience.canRedeem">
            <p>
              Seu saldo atual é de <strong>{{ formatPoints(balance) }}</strong> e seu saldo após o resgate será de
              <strong>{{ formatPoints(balanceAfter) }}</strong>.
            </p>
            <div class="ds-note">
              <i class="bx bx-info-circle" aria-hidden="true"></i>
              <span>Seus pontos serão debitados agora e a compra ficará aguardando aprovação da equipe.</span>
            </div>
            <b-form-checkbox v-model="accepted" class="mb-3">Li as observações e regras</b-form-checkbox>
            <button class="btn btn-default btn-lg btn-block text-uppercase" :disabled="!accepted || submitting" v-on:click="redeem()">
              Confirmar resgate
              <b-spinner v-if="submitting" small class="ml-2 align-middle" variant="white" role="status"></b-spinner>
            </button>
          </template>
          <template v-else>
            <div class="ds-note">
              <i class="bx bx-error-circle text-danger" aria-hidden="true"></i>
              <span v-if="experience.soldOut">Esta experiência está indisponível no momento.</span>
              <span v-else>Saldo de pontos insuficiente. Faltam {{ formatPoints(experience.missingPoints) }}.</span>
            </div>
            <router-link tag="a" to="/experiences" class="btn btn-default btn-lg btn-block text-uppercase">Ver experiências</router-link>
          </template>
          <router-link tag="a" :to="'/experiences/' + experience.id" class="btn btn-outline-default btn-lg btn-block text-uppercase mt-2">Voltar</router-link>
        </div>
      </div>
    </div>

    <b-modal id="ex-processing" modal-class="ds-modal" centered hide-footer hide-header no-close-on-backdrop no-close-on-esc>
      <div class="text-center py-4">
        <b-spinner variant="default" class="mb-3" role="status"></b-spinner>
        <p class="mb-0">Aguarde! Estamos registrando o seu resgate.</p>
      </div>
    </b-modal>
  </Layout>
</template>
